import { useMutation, useQuery } from '@tanstack/react-query';

import {
  createWebSheetBrand,
  createWebSheetLocalisations,
  createWebSheetSettingsByBrandId,
  deleteWebSheetBrand,
  deleteWebSheetLocalisation,
  getWebsheetBrands,
  getWebSheetLocalisations,
  getWebsheetSettingsByBrandId,
  setPrimaryWebSheetBrand,
  updateWebSheetBrand,
  updateWebSheetLocalisations,
  updateWebSheetSettingsByBrandId,
  uploadLogo,
} from '../../services';

export const useCreateLocalisation = () => {
  return useMutation({
    mutationKey: ['create_localisation'],
    mutationFn: createWebSheetLocalisations,
  });
};

export const useUpdateLocalisation = () => {
  return useMutation({
    mutationKey: ['update_localisation'],
    mutationFn: updateWebSheetLocalisations,
  });
};

export const useDeleteLocalisation = () => {
  return useMutation({
    mutationKey: ['delete_localisation'],
    mutationFn: deleteWebSheetLocalisation,
  });
};

export const useGetWebsheetBrands = (environment_id: string | undefined) => {
  return useQuery({
    queryKey: ['get_websheet_brands', environment_id],
    queryFn: () => getWebsheetBrands(environment_id),
    staleTime: 0,
    retry: 1,
    enabled: Boolean(environment_id),
    refetchOnWindowFocus: false,
  });
};

export const useUpdateWebSheetBrand = () => {
  return useMutation({
    mutationKey: ['update_websheet_brand'],
    mutationFn: updateWebSheetBrand,
  });
};

export const useDeleteWebSheetBrand = () => {
  return useMutation({
    mutationKey: ['delete_websheet_brand'],
    mutationFn: deleteWebSheetBrand,
  });
};

export const useCreateWebSheetBrand = () => {
  return useMutation({
    mutationKey: ['create_websheet_brand'],
    mutationFn: createWebSheetBrand,
  });
};

export const useSetPrimaryWebSheetBrand = () => {
  return useMutation({
    mutationKey: ['set_primary_websheet_brand'],
    mutationFn: setPrimaryWebSheetBrand,
  });
};

export const useGetWebsheetSettingsByBrandId = ({
  environmentId,
  brandId,
  tag,
}: {
  environmentId: string | undefined;
  brandId: string | undefined;
  tag: string | undefined;
}) => {
  return useQuery({
    queryKey: [
      'get_websheet_settings_by_brand_id',
      environmentId,
      brandId,
      tag,
    ],
    queryFn: async () => {
      const params = { environmentId, brandId, tag };

      const response = await Promise.allSettled([
        getWebSheetLocalisations(params),
        getWebsheetSettingsByBrandId(params),
      ]);

      return response;
    },
    retry: 1,
    select: data => {
      const [localisationResult, useCaseResult] = data;

      const localisationSettings =
        localisationResult.status === 'fulfilled'
          ? localisationResult.value || []
          : [];
      const useCaseSettings =
        useCaseResult.status === 'fulfilled' ? useCaseResult.value : {};


      return {
        useCaseSettings,
        localisationSettings,
        emptyUseCaseSettings: !useCaseSettings || Object.keys(useCaseSettings).length === 0,
        emptyLocalisationSettings: localisationSettings.length === 0,
      };
    },
    enabled: Boolean(brandId) && Boolean(environmentId) && Boolean(tag),
    refetchOnWindowFocus: false,
  });
};

export const useUpdateWebSheetSettings = () => {
  return useMutation({
    mutationKey: ['update_websheet_settings'],
    mutationFn: updateWebSheetSettingsByBrandId,
  });
};

export const useCreateWebsheetSettings = () => {
  return useMutation({
    mutationKey: ['create_websheet_settings'],
    mutationFn: createWebSheetSettingsByBrandId,
  });
};

export const useUploadWebsheetLogo = () => {
  return useMutation({
    mutationKey: ['upload_websheet_logo'],
    mutationFn: uploadLogo,
  });
};
